/**
 * Offline write-queue backed by IndexedDB.
 * Captures POST/PATCH/PUT/DELETE calls that fail due to network errors and
 * replays them automatically when the device comes back online.
 *
 * Why IndexedDB and not localStorage:
 *   - Survives across tabs and sessions
 *   - Async (doesn't block the UI thread)
 *   - Holds much larger payloads (signatures, photos can be 100KB+ each)
 *
 * Why this exists:
 *   - SXM cellular signal drops constantly on roofs and in concrete buildings.
 *   - Employees can keep working — clicking "complete WO", logging hours,
 *     uploading photos — and nothing is lost; everything flushes on reconnect.
 */
import { openDB } from "idb";

const DB_NAME = "pb-offline";
const STORE = "queue";

// Endpoints that aren't worth queuing (idempotency would be weird or the
// payload is time-sensitive like a GPS ping)
const SKIP_PATTERNS = [
  /\/gps\/positions$/,        // live GPS — stale pings are useless
  /\/auth\/dev-login$/,
  /\/auth\/logout$/,
  /\/payments\/checkout$/,    // Stripe sessions must be live
  /\/notifications\/.*\/read$/,
];

async function db() {
  return openDB(DB_NAME, 1, {
    upgrade(d) {
      if (!d.objectStoreNames.contains(STORE)) {
        d.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    },
  });
}

export function shouldSkipQueue(method, url) {
  if ((method || "GET").toUpperCase() === "GET") return true;
  return SKIP_PATTERNS.some((rx) => rx.test(url || ""));
}

export async function enqueue(item) {
  const d = await db();
  const id = await d.add(STORE, {
    ...item,
    created_at: Date.now(),
    attempts: 0,
  });
  notify();
  return id;
}

export async function list() {
  const d = await db();
  return d.getAll(STORE);
}

export async function count() {
  const d = await db();
  return d.count(STORE);
}

export async function remove(id) {
  const d = await db();
  await d.delete(STORE, id);
  notify();
}

export async function clearAll() {
  const d = await db();
  await d.clear(STORE);
  notify();
}

// ---- subscribe / notify ----------
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  count().then((n) => listeners.forEach((fn) => fn(n)));
}

// ---- flush ----------
let flushing = false;
export async function flush(axiosInstance) {
  if (flushing) return { ok: 0, failed: 0 };
  flushing = true;
  let ok = 0;
  let failed = 0;
  try {
    const items = await list();
    items.sort((a, b) => a.id - b.id); // FIFO
    for (const item of items) {
      try {
        await axiosInstance.request({
          method: item.method,
          url: item.url,
          data: item.data,
          headers: { ...(item.headers || {}), "x-pb-replay": "1" },
        });
        await remove(item.id);
        ok++;
      } catch (err) {
        // Retry budget: 5 attempts then drop & surface a one-time error
        const next = (item.attempts || 0) + 1;
        if (next >= 5) {
          await remove(item.id);
          failed++;
          console.error(`Offline queue: gave up on ${item.method} ${item.url} after 5 attempts`, err);
        } else {
          const d = await db();
          await d.put(STORE, { ...item, attempts: next });
        }
        // If we hit a network error mid-flush, stop — try again later
        if (!err?.response) break;
      }
    }
  } finally {
    flushing = false;
    notify();
  }
  return { ok, failed };
}
